import Point from "esri/geometry/Point";
import Polygon from "esri/geometry/Polygon";

// General --------------------------------------------------
export enum nvrTools {
  removal = "Removal",
  offset = "Offset",
}

export type nvrToolModes = nvrTools;

export enum detailedUploadOptions {
  yes = "Yes",
  no = "No",
}

export type detailedUploadOption = detailedUploadOptions;

export enum pastRemovalOptions {
  yes = "Yes",
  no = "No",
}

export type pastRemovalOption = pastRemovalOptions;

export enum uploadTypes {
  simple = "Simple",
  detailed = "Detailed",
}

export type uploadTypesOption = uploadTypes;

export enum featureTypes {
  patch = "patch",
  tree = "tree",
  revegetationArea = "revegetationArea",
}

export enum mapAdditionalModes {
  identify = "identify",
  measure = "measure",
  edit = "edit",
}

export type mapModes = featureTypes | mapAdditionalModes;

export enum basemapTypes {
  esriTile,
  wmts,
}

// Features --------------------------------------------------
export enum featureSource {
  Drawn = "Drawn",
  Uploaded = "Uploaded",
}

export enum treeType {
  Patch = "Patch",
  Scattered = "Scattered",
}

export enum treeCmType {
  DBH = "DBH",
  CIR = "CIR",
}

export enum treeSize {
  Lg = "Lg",
  Sm = "Sm",
}

export interface Feature {
  id?: string;
  source: featureSource;
  geom?: Point | Polygon;
}

export interface Patch extends Feature {
  area: number;

  // fields for detailed features
  site_id?: string;
  zone_id?: string;
  prop_id?: string;
  type?: string;
  cp?: string;
  veg_codes?: string;
  is_past?: string;
  is_partial?: string;
  lt_count?: number;
  cond_score?: number;
  cond_part?: number;
  surv_date?: number;
  exc_hims?: string;
  vlot?: number;
  lot?: number;
  recruits?: number;
  gain_score?: number;
  inc_hims?: string;
}

export interface Tree extends Feature {
  treeType: treeType;
  cm: number;
  cmType?: treeCmType;
  size?: treeSize;
  vegCode: string | undefined;
  benchmark: string | undefined;
  benchmarkCm: number | undefined;

  // fields for detailed features
  site_id?: string;
  zone_id?: string;
  prop_id?: string;
  type?: string;
  meas_type?: string;
  meas?: number;
  lt_val?: string;
  cp?: string;
  veg_codes?: string;
  is_past?: string;
  is_partial?: string;
  lt_count?: number;
  cond_score?: number;
  cond_part?: number;
  surv_date?: number;
  exc_hims?: string;
  vlot?: number;
  lot?: number;
  recruits?: number;
  gain_score?: number;
  inc_hims?: string;
  hasMultipleBenchmarks?: boolean; // make these trees editable in detailed upload
}

export interface RevegetationArea extends Feature {
  area: number;
  apRatio: number;

  // fields for detailed features
  site_id?: string;
  zone_id?: string;
  prop_id?: string;
  vlot?: number;
  lot?: number;
  recruits?: number;
  type?: string;
  cp?: string;
  veg_codes?: string;
  cond_score?: number;
  gain_score?: number;
  surv_date?: number;
  inc_hims?: string;
}

export enum featureTypeFieldValues {
  patch = "p",
  scatteredTree = "st",
  patchTree = "pt",
  revegetationArea = "rv",
}

export enum processingStatuses {
  processing = "processing",
  completed = "completed",
  failed = "failed",
}

export enum validations {
  invalidPolygon = "invalidPolygon",
  overlappingTypes = "overlappingTypes",
  msaLevyArea = "msaLevyArea",
  msaConservation = "msaConservation",
  msa12Precincts = "msa12Precincts",
  apRatio = "apRatio",
  treesBelowDbhThreshold = "treesBelowDbhThreshold",
  wetlands = "wetlands",
  nonWoodyEvc = "nonWoodyEvc",
  parcelBoundaryProximity = "parcelBoundaryProximity",
  outsideSupportingRasters = "outsideSupportingRasters",
}

export enum validationFeatureCreationContext {
  draw = "draw",
  edit = "edit",
  upload = "upload",
}

export interface ValidationResult {
  fail?: boolean;
  messageContent?: any;
  messageTitle?: string;
}
